import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from './config';
import './FacialRecognition.css';

const FacialRecognition = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [scanImage, setScanImage] = useState(null);
  const [scanImagePreview, setScanImagePreview] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [similarityThreshold, setSimilarityThreshold] = useState(50);
  const [embeddingStatus, setEmbeddingStatus] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  // Fetch user's events
  const fetchEvents = async () => {
    try {
      // Two bugs lived here. `Buffer` is a Node global that does not exist in a
      // browser bundle, so decoding the token threw on every render and this
      // page never loaded a single event; and the user id being reconstructed
      // (decode the token for an email, list every user, match on it) is
      // already in localStorage, where every other screen reads it from.
      const userId = localStorage.getItem('userId');
      if (!userId) return;

      const response = await fetch(`${API_BASE_URL}/api/events/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      // The endpoint returns { events: [...] }; assigning the envelope itself
      // left `events` as an object, so the list below rendered nothing.
      setEvents(Array.isArray(data.events) ? data.events : []);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  // Fetch embedding status for selected event
  const fetchEmbeddingStatus = async (eventId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/embedding-status`);
      const status = await response.json();
      setEmbeddingStatus(status);
    } catch (err) {
      console.error('Error fetching embedding status:', err);
    }
  };

  // Handle event selection
  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    fetchEmbeddingStatus(event.id);
    setSearchResults([]);
    setScanImage(null);
    setScanImagePreview(null);
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      processImageFile(file);
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processImageFile(e.target.files[0]);
    }
  };

  // Process image file
  const processImageFile = (file) => {
    setError('');

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setScanImage(e.target.result);
      setScanImagePreview(e.target.result);
      setSuccess('Face image ready for search');
    };
    reader.readAsDataURL(file);
  };

  // Perform facial recognition search
  const handleSearch = async () => {
    if (!scanImage || !selectedEvent) {
      setError('Please select both an event and a face image');
      return;
    }

    setSearching(true);
    setError('');
    setSuccess('');
    setSearchResults([]);

    try {
      const response = await fetch(`${API_BASE_URL}/api/search/by-face`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventId: selectedEvent.id,
          imageData: scanImage,
          threshold: similarityThreshold / 100
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.message.includes('No face detected')) {
          setError('No face detected in the provided image. Please upload a clear face photo.');
        } else {
          setError(data.message || 'Search failed');
        }
      } else {
        setSearchResults(data.matches || []);
        if (data.matches && data.matches.length > 0) {
          setSuccess(`Found ${data.matches.length} matching images! Sorted by similarity.`);
        } else {
          setSuccess(`No matches found above ${similarityThreshold}% similarity threshold`);
        }
      }
    } catch (err) {
      setError('Search error: ' + err.message);
    } finally {
      setSearching(false);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setScanImage(null);
    setScanImagePreview(null);
    setSearchResults([]);
    setSuccess('');
    setError('');
  };

  // Copy similarity score to clipboard
  const copySimilarityScore = (similarity) => {
    navigator.clipboard.writeText(`${similarity}%`);
  };

  return (
    <div className="facial-recognition-container">
      {/* Header */}
      <div className="fr-header">
        <div className="fr-header-content">
          <h1>🔍 Facial Recognition Search</h1>
          <p className="fr-subtitle">Find photos by scanning a face</p>
        </div>
        <button className="fr-back-btn" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
      </div>

      {/* Main content */}
      <div className="fr-content">
        {/* Left panel - Controls */}
        <div className="fr-left-panel">
          {/* Event selector */}
          <div className="fr-section">
            <h2>Step 1: Select Event</h2>
            <div className="events-grid">
              {events.length === 0 ? (
                <p className="no-events">No events found. <a href="/events">Create an event first</a></p>
              ) : (
                events.map(event => (
                  <div
                    key={event.id}
                    className={`event-card ${selectedEvent?.id === event.id ? 'selected' : ''}`}
                    onClick={() => handleEventSelect(event)}
                  >
                    <div className="event-card-content">
                      <h3>{event.event_name}</h3>
                      <p className="event-couple">{event.groom_name} & {event.bridal_name}</p>
                      <p className="event-date">{new Date(event.event_date).toLocaleDateString()}</p>
                    </div>
                    {selectedEvent?.id === event.id && (
                      <div className="event-selection-indicator">✓</div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Embedding status */}
            {selectedEvent && embeddingStatus && (
              <div className={`embedding-status ${embeddingStatus.embeddingStatus === 'READY' ? 'ready' : 'pending'}`}>
                <div className="status-icon">{embeddingStatus.embeddingStatus === 'READY' ? '✓' : '⏳'}</div>
                <div className="status-info">
                  <p className="status-label">{embeddingStatus.embeddingStatus}</p>
                  <p className="status-detail">{embeddingStatus.imagesWithFaces} / {embeddingStatus.totalImages} images processed</p>
                </div>
              </div>
            )}
          </div>

          {/* Image upload */}
          {selectedEvent && embeddingStatus?.embeddingStatus === 'READY' && (
            <div className="fr-section">
              <h2>Step 2: Scan Face</h2>
              <div
                className={`upload-area ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="face-input"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                {scanImagePreview ? (
                  <div className="preview-container">
                    <img src={scanImagePreview} alt="Scan preview" className="preview-image" />
                    <button
                      type="button"
                      className="clear-btn"
                      onClick={handleClearSearch}
                    >
                      Clear & Upload New
                    </button>
                  </div>
                ) : (
                  <label htmlFor="face-input" className="upload-label">
                    <div className="upload-icon">📸</div>
                    <p className="upload-text">Drag face photo here or click to upload</p>
                    <p className="upload-hint">JPG, PNG, or WebP (clear face required)</p>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Similarity threshold */}
          {selectedEvent && embeddingStatus?.embeddingStatus === 'READY' && (
            <div className="fr-section">
              <h2>Step 3: Search</h2>
              <div className="threshold-control">
                <label>
                  Accuracy Level: <strong>{similarityThreshold}%</strong>
                </label>
                <input
                  type="range"
                  min="20"
                  max="90"
                  value={similarityThreshold}
                  onChange={(e) => setSimilarityThreshold(parseInt(e.target.value))}
                  className="threshold-slider"
                />
                <p className="threshold-hint">
                  {similarityThreshold >= 70
                    ? '🎯 Very strict — only exact same face'
                    : similarityThreshold >= 50
                    ? '✅ Recommended — accurate, no wrong faces'
                    : similarityThreshold >= 35
                    ? '⚠️ Moderate — may include similar faces'
                    : '🔍 Loose — broader search, less precise'}
                </p>
              </div>

              <button
                className="search-btn"
                onClick={handleSearch}
                disabled={!scanImage || searching}
              >
                {searching ? '🔄 Searching...' : '🔍 Search for Matches'}
              </button>
            </div>
          )}

          {/* Status messages */}
          {error && <div className="fr-error">{error}</div>}
          {success && !searchResults.length && <div className="fr-success">{success}</div>}
        </div>

        {/* Right panel - Results */}
        <div className="fr-right-panel">
          <div className="fr-section full-height">
            <h2>Results</h2>
            
            {searchResults.length > 0 ? (
              <div className="results-container">
                <div className="results-summary">
                  <p className="results-count">Found <strong>{searchResults.length}</strong> matching images</p>
                  {success && <p className="results-message">{success}</p>}
                </div>
                
                <div className="results-gallery">
                  {searchResults.map((image, idx) => (
                    <div key={image.id} className="result-card">
                      <div className="result-image-wrapper">
                        <img
                          src={image.thumbnail_url || image.image_url}
                          alt={`Match ${idx + 1}`}
                          className="result-image"
                        />
                        <div className="similarity-badge">
                          <div className="similarity-circle">
                            <span className="similarity-value" onClick={() => copySimilarityScore(image.similarity)}>
                              {image.similarity}%
                            </span>
                          </div>
                          <div className="confidence-label">{image.match_confidence}</div>
                        </div>
                      </div>
                      <div className="result-info">
                        <p className="result-detail">
                          <strong>Similarity:</strong> {image.similarity}%
                        </p>
                        <p className="result-detail">
                          <strong>Confidence:</strong> {image.match_confidence}
                        </p>
                        <p className="result-detail">
                          <strong>Date:</strong> {new Date(image.created_at).toLocaleDateString()}
                        </p>
                        <p className="result-detail">
                          <strong>Size:</strong> {(image.image_size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="results-empty">
                <div className="empty-icon">
                  {!selectedEvent
                    ? '👈'
                    : embeddingStatus?.embeddingStatus !== 'READY'
                    ? '⏳'
                    : !scanImage
                    ? '📸'
                    : '🔍'}
                </div>
                <p className="empty-text">
                  {!selectedEvent
                    ? 'Select an event to begin'
                    : embeddingStatus?.embeddingStatus !== 'READY'
                    ? `Processing images... ${embeddingStatus?.imagesWithFaces || 0}/${embeddingStatus?.totalImages || 0}`
                    : !scanImage
                    ? 'Upload a face photo to search'
                    : 'Click "Search for Matches" to find similar faces'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacialRecognition;
